import { useState } from "react";
import client from "@/lib/glossyApi";
import { toast } from "sonner";
import { Globe, Sparkles, RefreshCw, Check, Download } from "lucide-react";

const KEYS = ["business_name", "owner_name", "borough", "category", "business_category", "address", "bio", "personal_bio", "business_bio", "services", "offers", "email", "mobile", "landline", "instagram", "tiktok", "linkedin", "website"];
export const mergeProfile = (current, extracted, mode = "blanks") => {
  const out = { ...current };
  KEYS.forEach((k) => {
    const v = extracted?.[k];
    if (typeof v !== "string" || !v.trim()) return;
    if (mode === "replace" || !String(out[k] || "").trim()) out[k] = v.trim();
  });
  return out;
};

const CHIP_LABELS = { business_name: "Business", owner_name: "Owner", borough: "Location", category: "Trade", business_category: "Category", email: "Email", mobile: "Mobile", landline: "Landline", instagram: "Instagram", tiktok: "TikTok", linkedin: "LinkedIn", offers: "Offer", address: "Address" };
const LONG_LABELS = { bio: "Story / bio", personal_bio: "Personal bio", business_bio: "Business bio", services: "Services" };

export default function WebsiteImport({ defaultUrl = "", dark = false, onApply, importPhotos, onSelect, self = false }) {
  const [url, setUrl] = useState(defaultUrl);
  const [scanning, setScanning] = useState(false);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [importing, setImporting] = useState(false);
  const [applied, setApplied] = useState(false);

  const bd = dark ? "border-cream/30" : "border-ink/20";
  const sub = dark ? "text-cream/60" : "text-ink/55";
  const chipBg = dark ? "bg-cream/10 text-cream/85" : "bg-secondary/70 text-ink/80";

  const scan = async () => {
    if (!url.trim()) { toast.error("Paste their website address first."); return; }
    setScanning(true); setData(null); setApplied(false); setSel([]);
    try {
      const r = await client.post("/import/website-preview", { url: url.trim() });
      setData(r.data);
      const initial = (r.data.photos || []).slice(0, 3);
      setSel(initial);
      onSelect?.(initial, r.data.logo_url || "");
      toast.success(self ? "We've read your site — review the draft below." : "Website read — review what the AI found below.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read that website — it may block robots. Try another page or fill in manually."); }
    setScanning(false);
  };

  const toggle = (u) => setSel((s) => {
    let next;
    if (s.includes(u)) next = s.filter((x) => x !== u);
    else if (s.length >= 5) { toast.error("Max 5 photos."); return s; }
    else next = [...s, u];
    onSelect?.(next, data?.logo_url || "");
    return next;
  });

  const apply = (mode) => {
    onApply(data.profile || {}, sel, mode, data.logo_url || "");
    setApplied(true);
    toast.success(mode === "replace"
      ? "Profile replaced with what we found — review below, then send to the studio."
      : (self ? "Filled the blanks from your website — anything you'd already written was kept." : "Profile filled from their website — review, tweak and save."));
  };

  const doImport = async () => {
    if (!sel.length) { toast.error("Tap the photos you want first."); return; }
    setImporting(true);
    try { await importPhotos(sel); toast.success(`${sel.length} photo${sel.length > 1 ? "s" : ""} imported from the website.`); }
    catch (e) { toast.error(e.response?.data?.detail || "Photo import failed — some sites block downloads."); }
    setImporting(false);
  };

  const found = data ? Object.entries(CHIP_LABELS).filter(([k]) => (data.profile?.[k] || "").trim()) : [];
  const longs = data ? Object.entries(LONG_LABELS).filter(([k]) => (data.profile?.[k] || "").trim()) : [];

  return (
    <div className={`border ${bd} p-5`} data-testid="website-import">
      <p className="overline text-gloss-gold flex items-center gap-2"><Globe size={14} /> {self ? "Quick start — from your website" : "Magic import — from their website"}</p>
      <p className={`font-sans text-sm ${sub} mt-1.5`}>{self ? "Paste your website and our editorial desk drafts your profile from it — nothing is saved until you review and apply." : "Paste a website and the AI reads it — bios, details and photos are drafted for you. Nothing is saved until you apply and review it."}</p>
      <div className="flex flex-col sm:flex-row gap-3 mt-4">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); scan(); } }}
          placeholder="e.g. www.theirbusiness.co.uk"
          className={`flex-1 bg-transparent border ${bd} px-4 py-3 font-sans focus:outline-none focus:border-gloss-gold`}
          data-testid="wi-url"
        />
        <button type="button" onClick={scan} disabled={scanning || !url.trim()} className={`${dark ? "bg-cream text-ink hover:bg-gloss-gold" : "bg-ink text-cream hover:bg-gloss-gold hover:text-ink"} px-6 py-3 overline flex items-center justify-center gap-2 transition-colors disabled:opacity-60`} data-testid="wi-scan">
          {scanning ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />} {scanning ? (self ? "Reading your site…" : "Reading their site…") : (self ? "Draft my profile" : "Scan with AI")}
        </button>
      </div>

      {data && (
        <div className="mt-5 space-y-4" data-testid="wi-results">
          {found.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {data.logo_url && <span className={`${chipBg} px-3 py-1.5 font-sans text-xs`} data-testid="wi-chip-logo"><b>Logo:</b> found ✓</span>}
              {found.map(([k, label]) => (
                <span key={k} className={`${chipBg} px-3 py-1.5 font-sans text-xs`} data-testid={`wi-chip-${k}`}><b>{label}:</b> {data.profile[k].length > 42 ? data.profile[k].slice(0, 42) + "…" : data.profile[k]}</span>
              ))}
            </div>
          )}
          {longs.map(([k, label]) => (
            <div key={k}>
              <span className="overline text-gloss-gold text-[10px]">{label}</span>
              <p className={`font-sans text-sm ${sub} mt-0.5 whitespace-pre-line`}>{data.profile[k].length > 300 ? data.profile[k].slice(0, 300) + "…" : data.profile[k]}</p>
            </div>
          ))}
          <div className="flex flex-col sm:flex-row gap-3">
            <button type="button" onClick={() => apply("blanks")} className={`border ${dark ? "border-cream/50 hover:bg-cream/10" : "border-ink hover:bg-ink hover:text-cream"} px-6 py-3 overline flex items-center justify-center gap-2 transition-colors`} data-testid="wi-apply">
              {applied ? <Check size={14} className="text-gloss-gold" /> : <Sparkles size={14} />} {applied ? "Applied — review below" : "Fill the blanks only"}
            </button>
            <button type="button" onClick={() => { if (window.confirm("Replace everything in the form with what we found on this website? Anything typed before will be overwritten.")) apply("replace"); }} className={`${dark ? "bg-cream text-ink hover:bg-gloss-gold" : "bg-gloss-gold text-ink hover:bg-ink hover:text-cream"} px-6 py-3 overline flex items-center justify-center gap-2 transition-colors`} data-testid="wi-apply-replace">
              <RefreshCw size={14} /> Use this website — replace everything
            </button>
          </div>
          <p className={`font-sans text-xs ${sub} -mt-1`}>"Fill the blanks" keeps anything already written. "Replace everything" starts the form fresh from this website.</p>

          {(data.photos || []).length > 0 && (
            <div>
              <p className={`overline ${sub} mb-2`}>Photos found on {self ? "your" : "their"} site — tap to pick (max 5)</p>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="wi-photos">
                {data.photos.map((u, i) => (
                  <button type="button" key={u} onClick={() => toggle(u)} className={`relative aspect-square overflow-hidden border-2 ${sel.includes(u) ? "border-gloss-gold" : "border-transparent opacity-70 hover:opacity-100"} transition-all`} data-testid={`wi-photo-${i}`}>
                    <img src={u} alt="" loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.closest("button").style.display = "none"; }} />
                    {sel.includes(u) && <span className="absolute top-1 right-1 bg-gloss-gold text-ink rounded-full p-0.5"><Check size={11} /></span>}
                  </button>
                ))}
              </div>
              {importPhotos ? (
                <button type="button" onClick={doImport} disabled={importing} className={`mt-3 border ${dark ? "border-cream/50 hover:bg-cream/10" : "border-ink/50 hover:border-ink"} px-5 py-2.5 overline flex items-center gap-2 transition-colors disabled:opacity-60`} data-testid="wi-import-photos">
                  {importing ? <RefreshCw size={13} className="animate-spin" /> : <Download size={13} />} Import {sel.length} selected photo{sel.length === 1 ? "" : "s"}
                </button>
              ) : (
                <p className={`font-sans text-xs ${sub} mt-2`}>Your picked photos will be pulled in automatically once the profile is created.</p>
              )}
            </div>
          )}
          {(data.photos || []).length === 0 && <p className={`font-sans text-xs ${sub}`}>No usable photos found on the site — upload your own below.</p>}
        </div>
      )}
    </div>
  );
}
